import { useInfiniteQuery, useQuery, type QueryClient } from "@tanstack/react-query";
import { isApiError } from "./client";
import { queryKeys } from "./queries";
import { storeOffersKeys } from "./storeOffers";
import {
  fetchCampaignReport,
  fetchCampaignReviewQueue,
  fetchEarnCoins,
  fetchHouseCampaigns,
  fetchMyCampaigns,
  fetchPartnerKeys,
  fetchPropertyOffers,
  isRouteMissing,
} from "./rewards";

/** Query keys and hooks for sponsored tasks (ADR-0011). */
export const rewardKeys = {
  all: ["rewards"] as const,
  propertyOffersAll: ["rewards", "property-offers"] as const,
  propertyOffers: (propertyId: string) => ["rewards", "property-offers", propertyId] as const,
  earn: ["rewards", "earn"] as const,
  attempt: (attemptId: string) => ["rewards", "attempt", attemptId] as const,
  myCampaigns: ["rewards", "my-campaigns"] as const,
  report: (campaignId: string) => ["rewards", "report", campaignId] as const,
  partnerKeys: (advertiserId: string) => ["rewards", "partner-keys", advertiserId] as const,
  reviewQueue: ["rewards", "review-queue"] as const,
  house: ["rewards", "house"] as const,
};

/** Never retry a refusal, a missing route (feature not deployed) or a missing resource. */
const noRetry = (count: number, err: unknown) => !(isApiError(err) && [401, 403, 404, 422].includes(err.status)) && !isRouteMissing(err) && count < 2;

/** Offers are live data (budgets run out, campaigns pause): re-read every minute while shown. */
export const OFFERS_REFRESH_MS = 60_000;

export function usePropertyOffersQuery(propertyId: string | null, enabled = true) {
  return useQuery({
    queryKey: rewardKeys.propertyOffers(propertyId ?? "none"),
    queryFn: ({ signal }) => fetchPropertyOffers(propertyId as string, signal),
    enabled: Boolean(propertyId) && enabled,
    staleTime: 15_000,
    refetchInterval: propertyId && enabled ? OFFERS_REFRESH_MS : false,
    retry: noRetry,
  });
}

export function useEarnCoinsQuery(enabled: boolean) {
  return useQuery({
    queryKey: rewardKeys.earn,
    queryFn: ({ signal }) => fetchEarnCoins(signal),
    enabled,
    staleTime: 10_000,
    refetchInterval: enabled ? OFFERS_REFRESH_MS : false,
    retry: noRetry,
  });
}

export function useMyCampaignsQuery(enabled: boolean) {
  return useQuery({
    queryKey: rewardKeys.myCampaigns,
    queryFn: fetchMyCampaigns,
    enabled,
    staleTime: 10_000,
    // Review decisions, fundings and takeovers happen elsewhere: keep the dashboard honest while open.
    refetchInterval: enabled ? 60_000 : false,
    retry: noRetry,
  });
}

export function useCampaignReportQuery(campaignId: string | null) {
  return useQuery({
    queryKey: rewardKeys.report(campaignId ?? "none"),
    queryFn: () => fetchCampaignReport(campaignId as string),
    enabled: Boolean(campaignId),
    staleTime: 30_000,
    retry: noRetry,
  });
}

export function usePartnerKeysQuery(advertiserId: string | null) {
  return useQuery({
    queryKey: rewardKeys.partnerKeys(advertiserId ?? "none"),
    queryFn: () => fetchPartnerKeys(advertiserId as string),
    enabled: Boolean(advertiserId),
    staleTime: 15_000,
    retry: noRetry,
  });
}

export function useCampaignReviewQueueQuery(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: rewardKeys.reviewQueue,
    queryFn: ({ pageParam }) => fetchCampaignReviewQueue(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled,
    retry: noRetry,
  });
}

export function useHouseCampaignsQuery(enabled: boolean) {
  return useQuery({ queryKey: rewardKeys.house, queryFn: fetchHouseCampaigns, enabled, staleTime: 10_000, retry: noRetry });
}

/** After a claim, a start, a cancel or an expiry: balances, the earn list and store offers change. */
export function invalidateAfterAttempt(qc: QueryClient, propertyId?: string | null): void {
  void qc.invalidateQueries({ queryKey: queryKeys.wallets });
  void qc.invalidateQueries({ queryKey: ["wallets", "history"] });
  void qc.invalidateQueries({ queryKey: rewardKeys.earn });
  if (propertyId) void qc.invalidateQueries({ queryKey: rewardKeys.propertyOffers(propertyId) });
  else void qc.invalidateQueries({ queryKey: rewardKeys.propertyOffersAll });
}

/** After a campaign change (edit, submit, fund, activate, pause, review): owner, staff and public views. */
export function invalidateCampaigns(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: rewardKeys.myCampaigns });
  void qc.invalidateQueries({ queryKey: rewardKeys.reviewQueue });
  void qc.invalidateQueries({ queryKey: rewardKeys.house });
  void qc.invalidateQueries({ queryKey: rewardKeys.earn });
  void qc.invalidateQueries({ queryKey: rewardKeys.propertyOffersAll });
  void qc.invalidateQueries({ queryKey: ["rewards", "report"] });
  // ADR-0027: the store screen's offers and status.
  void qc.invalidateQueries({ queryKey: storeOffersKeys.all });
}
